import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon.tsx";
import { Button, type ButtonVariant } from "./primitives.tsx";

/* ------------------------------------------------------------------ */
/* Toasts — success, 4 s, naming the action                            */
/* ------------------------------------------------------------------ */
export interface Toast {
  id: number;
  tone: "success" | "error" | "warning" | "info";
  text: ReactNode;
}
interface ToastApi {
  push: (tone: Toast["tone"], text: ReactNode, ms?: number) => void;
  success: (text: ReactNode) => void;
  error: (text: ReactNode) => void;
  warning: (text: ReactNode) => void;
  info: (text: ReactNode) => void;
}
const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);
  const push = useCallback((tone: Toast["tone"], text: ReactNode, ms = tone === "error" ? 7000 : 4000) => {
    const id = ++seq.current;
    setItems((xs) => [...xs, { id, tone, text }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), ms);
  }, []);
  const api = useMemo<ToastApi>(() => ({ push, success: (t) => push("success", t), error: (t) => push("error", t), warning: (t) => push("warning", t), info: (t) => push("info", t) }), [push]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <Icon name={t.tone === "success" ? "check" : t.tone === "error" || t.tone === "warning" ? "alert" : "info"} size={16} />
            <div>{t.text}</div>
            <button type="button" aria-label="Dismiss" onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}>
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast outside ToastProvider");
  return ctx;
}

/* ------------------------------------------------------------------ */
/* Modal + confirm dialog                                              */
/* ------------------------------------------------------------------ */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    // focus the first field in the body, not the close button in the head; and only once, when the dialog opens
    const first = box.current?.querySelector(".modal-body")?.querySelector<HTMLElement>(FOCUSABLE) ?? box.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? box.current)?.focus();
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") return close.current();
      if (e.key !== "Tab" || !box.current) return;
      const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!items.length) return;
      const a = items[0]!;
      const z = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener("keydown", h);
    return () => { document.removeEventListener("keydown", h); opener?.focus?.(); };
  }, [open]);
  if (!open) return null;
  return (
    <div className="modal-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={box} tabIndex={-1} className={`modal${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="icon-btn plain" aria-label="Close" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export interface ConfirmOptions {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ButtonVariant;
  /** Ask for a reason; resolves with the text. */
  reason?: { label: string; required?: boolean; placeholder?: string };
  /** An optional yes/no checkbox; resolves with `extra`. */
  extra?: { label: ReactNode; defaultChecked?: boolean };
}
type ConfirmFn = (opts: ConfirmOptions) => Promise<{ ok: boolean; reason?: string; extra?: boolean }>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (r: { ok: boolean; reason?: string; extra?: boolean }) => void }) | null>(null);
  const [reason, setReason] = useState("");
  const [extra, setExtra] = useState(false);
  const confirm = useCallback<ConfirmFn>((opts) => new Promise((resolve) => { setReason(""); setExtra(!!opts.extra?.defaultChecked); setState({ ...opts, resolve }); }), []);
  const close = (ok: boolean) => {
    state?.resolve({ ok, reason: reason.trim() || undefined, extra: state.extra ? extra : undefined });
    setState(null);
  };
  const needReason = !!state?.reason?.required && !reason.trim();
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <Modal
          open
          onClose={() => close(false)}
          title={state.title}
          footer={
            <>
              <Button variant="ghost" onClick={() => close(false)}>
                {state.cancelLabel ?? "Cancel"}
              </Button>
              <Button variant={state.tone ?? "primary"} disabled={needReason} onClick={() => close(true)}>
                {state.confirmLabel ?? "Confirm"}
              </Button>
            </>
          }
        >
          {state.body && <div className="text-2" style={{ fontSize: 13.5 }}>{state.body}</div>}
          {state.reason && (
            <div className="field mt-4">
              <label>
                {state.reason.label} {state.reason.required && <span className="req">*</span>}
              </label>
              <textarea className="textarea" value={reason} placeholder={state.reason.placeholder} onChange={(e) => setReason(e.target.value)} autoFocus />
            </div>
          )}
          {state.extra && (
            <label className="check-row mt-3">
              <input type="checkbox" checked={extra} onChange={(e) => setExtra(e.target.checked)} /> <span>{state.extra.label}</span>
            </label>
          )}
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm outside ConfirmProvider");
  return ctx;
}

/** Render a server error shape { error, message, details } as words a person can act on. */
export function errorMessage(e: unknown): string {
  if (!e) return "Something went wrong";
  if (typeof e === "string") return e;
  const err = e as { message?: string; error?: string; details?: Array<{ path: string; message: string }> | { fields?: string[] } };
  if (Array.isArray(err.details) && err.details.length) return err.details.map((d) => (d.path ? `${d.path}: ${d.message}` : d.message)).join("; ");
  return err.message ?? err.error ?? "Something went wrong";
}

/** Field-level errors from a validation response, keyed by path. */
export function fieldErrors(e: unknown): Record<string, string> {
  const err = e as { details?: Array<{ path: string; message: string }> };
  const out: Record<string, string> = {};
  if (Array.isArray(err?.details)) for (const d of err.details) if (d.path) out[d.path] = d.message;
  return out;
}
